import { Alert, Box, Button, Card, Collapse, Divider, Grid, Stack, Typography } from "@mui/material";
import { onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetAllGroupsInClass, GetClassRef } from "../FirebaseQueries";
import { Warning } from "@mui/icons-material";
import { GetScenario, Scenario, scenarioTitles } from "../Scenarios/Scenarios";
import dayjs from "dayjs";
import ClassProgressTasksRow from "../components/ClassProgressTasksRow";


interface IClassData {
	code: string;
	session: number;
	scenarioIndex: number;
	startTime: Date;
	endTime: Date;
}

interface GroupNumber {
	id: string;
	number: number;
}

export default function ClassProgress() {
	let params = useParams();
	const [classDeleted, setClassDeleted] = useState(false);
	const [classData, setClassData] = useState<IClassData>({
		code: "...",
		session: 1,
		scenarioIndex: 0,
		startTime: new Date(),
		endTime: new Date()
	});
	const [groupNumbers, setGroupNumbers] = useState<GroupNumber[]>([]);
	const [scenario, setScenario] = useState<Scenario>();

	const classId = (): string => {
		return params.classId !== undefined ? params.classId : "";
	}

	useEffect(() => { // Get Class
		const unsubscribe = onSnapshot(GetClassRef(classId()), (snapshot) => {
			if (snapshot.exists()) {
				setClassData({
					code: snapshot.data().code,
					session: snapshot.data().session,
					scenarioIndex: snapshot.data().scenarioIndex,
					startTime: snapshot.data().startTime.toDate(),
					endTime: snapshot.data().endTime.toDate()
				});
				setScenario(GetScenario(snapshot.data().scenarioIndex));
			} else {
				setClassDeleted(true);
			}
		})
		return unsubscribe;
	}, []);

	useEffect(() => { // Get Groups
		const classId = params.classId !== undefined ? params.classId : "";
		const q = GetAllGroupsInClass(classId);
		const unsubscribe = onSnapshot(q, (snapshot) => {
			if (!snapshot.empty) {
				let temp: GroupNumber[] = [];
				snapshot.forEach((doc) => {
					temp.push({
						id: doc.id,
						number: doc.data().groupNumber
					});
				});
				setGroupNumbers(temp);
			}
			else {
				// Do something if empty
			}
		});
		return unsubscribe;
	}, []);

	return (
		<>
			{/* ---------- Collapses ---------- */}
			<Collapse in={classDeleted}>
				<Alert icon={<Warning fontSize="inherit" />} severity="warning"
					action={
						<Button
							variant="outlined"
							aria-label="close"
							color="inherit"
							size="small"
							href="/app/classes"
						>
							Go back to Classes
						</Button>
					}>
					This class was deleted or does not exist.
				</Alert>
			</Collapse>
			{/* ---------- Main ---------- */}
			<Stack spacing={2}>
				{/* ---------- Info ---------- */}
				<Card>
					<Stack direction={"row"} spacing={2}>
						<Box sx={{ width: "100%" }}>
							<Typography variant="subtitle1" textAlign={"left"}>Progress</Typography>
							<Divider />
						</Box>
						<Stack direction={"row"} spacing={1}>
							<Button href={`/app/classes/${classId()}`} variant="contained" sx={{ minWidth: "150px" }}>Back to Class</Button>
							<Button href={`/app/classes/overview/${classId()}`} variant="contained" sx={{ minWidth: "150px" }}>View Results</Button>
						</Stack>
					</Stack>
					<Stack alignItems={"flex-start"} spacing={0.5}>
						<Stack direction={"row"} alignItems={"center"} spacing={4}>
							<Typography fontWeight={"bold"}>Class: {classData.code}</Typography>
							<Typography fontWeight={"bold"}>Session: {classData.session}</Typography>
							<Typography fontWeight={"bold"}>Start Time: {dayjs(classData.startTime).format("DD/MM/YYYY HH:mm A")}</Typography>
						</Stack>
						<Typography>Scenario: {scenarioTitles[classData.scenarioIndex]}</Typography>
						<Typography>Groups: {groupNumbers.length}</Typography>
					</Stack>
				</Card>
				{/* ---------- Progress ---------- */}
				<Card>
					<Grid container width={1} spacing={1}>
						{/* ---------- Groups ---------- */}
						<Grid size={1}>
							<Typography height="32px" marginBottom={2} variant="subtitle1">Groups</Typography>
							<Box height="32px"></Box>
							<Stack spacing={0.5}>
								{groupNumbers.sort((a, b) => a.number - b.number).map((group, index) => (
									<Stack key={index} height={"40px"} alignContent={"center"} justifyContent={"center"}>
										<Typography variant="subtitle1" fontWeight={"bold"}>{group.number}</Typography>
									</Stack>
								))}
							</Stack>
						</Grid>
						{/* ---------- Tasks ---------- */}
						<Grid size={10}>
							<Typography height="32px" marginBottom={2} variant="subtitle1" flexGrow={1}>Progress</Typography>
							<Grid height="32px" width={1} container>
								{scenario?.tasks.map((task) => (
									<Grid key={task.id} size={1} spacing={0.5}>
										<Typography variant="subtitle2">{task.name.split(":")[0]}</Typography>
									</Grid>
								))}
							</Grid>
							<Stack spacing={0.5}>
								{groupNumbers.sort((a, b) => a.number - b.number).map((group, index) => (
									<ClassProgressTasksRow key={index} groupId={group.id} scenario={scenario} />
								))}
							</Stack>
						</Grid>
						{/* ---------- Actions ---------- */}
						<Grid size={1}>
							<Box height="32px" marginBottom={2}></Box>
							<Box height="32px"></Box>
							<Stack spacing={0.5}>
								{groupNumbers.sort((a, b) => a.number - b.number).map((group, index) => (
									<Button variant="contained" key={index} href={"/app/groups/" + group.id}>Observe</Button>
								))}
							</Stack>
						</Grid>
					</Grid>
				</Card>
			</Stack>
		</>
	);
}